import { isBetaProdBuild } from "../../helpers/envHelper";

/**
 * Uplifted WHOLE from `apps/web/src/data/env-constants.ts`.
 *
 * WEB-ONLY — mobile has no env-constants file (it reads `react-native-config` through its own
 * `config/index.js`) — so this folder has a single `index.desktop.ts`. Eight web files import
 * it; the app file is now a re-export shim, so none of them changed.
 *
 * 🔴 **Everything here is computed at MODULE SCOPE, and that is why this module keeps its
 * direct `process.env` / `window` reads instead of going through the effects bridge.** The
 * bridges are wired after core modules evaluate (ledger hazard 18), so `getEnv()` would return
 * the empty default and every flag below would be permanently `false` — silently, on every
 * build. Making them lazy would fix the read but change `isProdEnv` from a boolean into a
 * function for eight importers, i.e. exactly the behaviour change this uplift must not make.
 *
 * Why the direct reads are nonetheless safe HERE, and only here:
 *   - `.desktop` ⇒ Metro never loads this file, so `process.env` is only ever read by webpack.
 *   - webpack's DefinePlugin is compilation-wide (craco sets no `noParse`, and `packages/core`
 *     is inside babel-loader's `include`), so `process.env.REACT_APP_ENVIRONMENT` is INLINED
 *     here exactly as it is in `apps/web/src` — verified by grepping the built bundle for the
 *     literal env string, with a control proving the grep can fail.
 *   - `window` is a real global at module-eval time in a browser.
 *
 * See `helpers/envHelper` for the same note from the other side.
 */

export const APP_ENV_TYPES = {
  LOCAL: "local",
  DEV: "dev",
  PREPROD: "preprod",
  STAGING: "staging",
  PROD: "prod",
  BETA: "beta",
} as const;
type AppEnvKeys = keyof typeof APP_ENV_TYPES;
export type AppEnvType = (typeof APP_ENV_TYPES)[AppEnvKeys];

const rawAppEnv: AppEnvType =
  (process.env.REACT_APP_ENVIRONMENT as AppEnvType) || APP_ENV_TYPES.LOCAL;

export const currentAppEnv: AppEnvType =
  rawAppEnv === APP_ENV_TYPES.PROD && isBetaProdBuild() ? APP_ENV_TYPES.BETA : rawAppEnv;

export const isProdEnv = currentAppEnv === APP_ENV_TYPES.PROD;
export const isBetaEnv = currentAppEnv === APP_ENV_TYPES.BETA;
export const isStagingEnv = currentAppEnv === APP_ENV_TYPES.STAGING;
export const isPreProdEnv = currentAppEnv === APP_ENV_TYPES.PREPROD;
export const isDevEnv = currentAppEnv === APP_ENV_TYPES.DEV;
export const isLocalEnv = currentAppEnv === APP_ENV_TYPES.LOCAL;

export const isBetaOrProdEnv = isProdEnv || isBetaEnv;

/**
 * Opt-in escape hatch for the top-level-browser gate in `apps/web/src/index.tsx`.
 *
 * That gate refuses to render in a plain browser tab outside prod/beta/staging/Electron,
 * so local web dev is normally only viewable through `pnpm desktop` or an iframe. For UI
 * work — kiosk screens especially — that is a heavy loop, and the alternative people
 * reach for is editing `index.tsx` and risking committing the bypass.
 *
 * DOUBLE-GATED ON PURPOSE: it needs the LOCAL env *and* an explicit opt-in, so it can
 * never be true in a deployed build even if the flag leaks into another env file.
 *
 *   # apps/web/.env.local
 *   REACT_APP_ALLOW_BROWSER_DEV = "true"
 *
 * ⚠️ Module-scope `process.env` read, same as everything above it — see the file header for
 * why that is correct here and does not generalise. DefinePlugin inlines it at build time.
 */
export const allowBrowserDevAccess =
  isLocalEnv && process.env.REACT_APP_ALLOW_BROWSER_DEV === "true";
